/**
 * "Is this batch of proposal cards still settling?"
 *
 * Under auto-accept a turn that raises three cards used to resolve them one
 * at a time and kick a continuation after each, so the next turn began while
 * the agent's own cards were still being applied: it saw one of three
 * resolved and the others open, and raised them again to be sure. Waiting
 * for the batch removes that state — the agent is asked to continue once,
 * with every card already applied.
 *
 * Pure and separate from the panel so the rule can be tested directly: the
 * cost of getting it wrong is a turn that never continues (see the third
 * proof run, 2026-09-10, which stalled after one turn because the card being
 * resolved was counted as still in flight).
 */

export interface SettlingCard {
  proposal_id: string;
  status: string;
  tool_name: string;
}

export interface SettlingInput {
  /** The card whose resolution we are reacting to — never counts itself. */
  excludePid: string;
  /** Cards whose auto-accept request is in the air right now. */
  inflight: readonly string[];
  /** Cards that could NOT be applied. They will never settle, so they must
   *  not be waited for — otherwise one failure strands the whole turn. */
  failed: readonly string[];
  /** Cards the PANEL knows have reached a decision, kept synchronously.
   *  `cards` comes from React state and lags a resolve by a render: the
   *  card was just approved, the state still says pending, and a check made
   *  in the same tick concludes the batch is still settling. Only a later
   *  re-check rescues it, and nothing guarantees one. This set is the truth
   *  the panel had in hand the moment the resolve returned. */
  decided?: readonly string[];
  /** Every card the panel knows about: the queue plus the open one. */
  cards: readonly SettlingCard[];
  /** A deletion never auto-applies: it always waits for a human. */
  isDelete: (toolName: string) => boolean;
}

export function batchStillSettling(input: SettlingInput): boolean {
  const { excludePid, inflight, failed, cards, isDelete } = input;
  const failedSet = new Set(failed);
  // A card that has already reached a decided state is NOT something to wait
  // for, even while an attempt against it is still in the air. The fast path
  // and the sweep both reach for the same card, so a duplicate attempt
  // re-enters an already-applied card into the in-flight set; counting that
  // made the genuinely-last card of a batch see a batch still settling. It
  // parked its continuation, nothing looked again, and the turn sat silent
  // until the user typed "continue" — on every turn that raised cards.
  const decided = new Set([
    ...cards.filter((c) => c.status !== 'pending').map((c) => c.proposal_id),
    ...(input.decided ?? []),
  ]);
  // THIS card is still in the in-flight set: the resolve that triggers the
  // check runs inside its own try block, before the finally that clears it.
  // Counting it would make every batch wait for itself.
  if (inflight.some(
    (pid) => pid !== excludePid && !decided.has(pid) && !failedSet.has(pid),
  )) return true;
  return cards.some((card) => (
    card.proposal_id !== excludePid
    && card.status === 'pending'
    && !decided.has(card.proposal_id)
    && !failedSet.has(card.proposal_id)
    && !isDelete(card.tool_name)
  ));
}

export default batchStillSettling;
